import React, { useState } from 'react';
import { submitToFormspree } from '../utils/formspree';
import ConsultationModal from '../components/ConsultationModal';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    honeypot: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const locations = [
    {
      name: 'Pottstown, PA',
      lines: ['247 East High Street', 'Pottstown, PA 19464'],
      map: 'https://www.google.com/maps?q=247%20East%20High%20Street%20Pottstown%20PA%2019464&output=embed',
    },
    {
      name: 'Teaneck, NJ',
      lines: ['154 West Englewood Ave', 'Teaneck, NJ 07666'],
      map: 'https://www.google.com/maps?q=154%20West%20Englewood%20Ave%20Teaneck%20NJ%2007666&output=embed',
    },
  ];

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    if (formData.honeypot) {
      setSubmitStatus({ success: true, message: 'Request sent successfully!' });
      setIsSubmitting(false);
      return;
    }

    try {
      await submitToFormspree({
        formType: 'Consultation Request',
        subject: `Consultation request from ${formData.name}`,
        fields: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
        },
      });

      setSubmitStatus({
        success: true,
        message: 'Request sent successfully! We typically respond within one business day.',
      });
      setFormData({ name: '', email: '', phone: '', message: '', honeypot: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus({ success: false, message: error.message || 'Failed to send message' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="page-hero">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(77,0,0,0.55),_transparent_50%),linear-gradient(135deg,#1a1214,#4D0000)]" />
        <div className="container relative z-10 mx-auto px-4">
          <p className="section-kicker text-primary-200">Contact</p>
          <h1 className="font-display text-5xl font-semibold md:text-6xl">Book a consultation</h1>
          <p className="mt-5 max-w-2xl text-xl text-white/85">
            Prefer to talk first? Call us, request a visit online, or send a question — we will guide you privately.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="rounded-full bg-white px-8 py-3 font-semibold text-primary-700 transition duration-300 hover:bg-primary-50"
            >
              Book Free Consultation
            </button>
            <a
              href="tel:+12017267342"
              className="rounded-full border border-white/70 px-8 py-3 text-center font-semibold text-white transition duration-300 hover:bg-white/10"
            >
              Call (201) 726-7342
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="section-kicker">Request a visit</p>
              <h2 className="section-heading">Tell us how to reach you</h2>
              <p className="mb-6 text-ink-muted">
                Share a few details and we will follow up to schedule your private consultation.
                Your information stays confidential.
              </p>

              {submitStatus && (
                <div
                  className={`mb-6 rounded-lg p-4 ${
                    submitStatus.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label className="mb-2 block text-ink-soft" htmlFor="name">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-4 py-2 focus:border-primary-500 focus:outline-none"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-ink-soft" htmlFor="email">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-4 py-2 focus:border-primary-500 focus:outline-none"
                    placeholder="Your email"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-ink-soft" htmlFor="phone">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-4 py-2 focus:border-primary-500 focus:outline-none"
                    placeholder="Your phone number"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-ink-soft" htmlFor="message">
                    What would you like to discuss?
                  </label>
                  <textarea
                    id="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-4 py-2 focus:border-primary-500 focus:outline-none"
                    placeholder="Coverage goals, preferred location, questions..."
                    required
                  ></textarea>
                </div>

                <div className="hidden" aria-hidden="true">
                  <label htmlFor="honeypot">Leave this empty</label>
                  <input
                    type="text"
                    id="honeypot"
                    name="honeypot"
                    value={formData.honeypot || ''}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <button
                  type="submit"
                  className="rounded-full bg-primary-600 px-8 py-3 font-semibold text-white transition duration-300 hover:bg-primary-700 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Request Consultation'}
                </button>
              </form>
            </div>

            <div>
              <p className="section-kicker">Visit or call</p>
              <h2 className="section-heading">Studios & hours</h2>

              <div className="space-y-8">
                <div>
                  <h3 className="font-display text-2xl font-semibold">Hours</h3>
                  <p className="mt-2 text-ink-muted">
                    Monday - Friday: 9:00 AM - 7:00 PM
                    <br />
                    Saturday: 9:00 AM - 5:00 PM
                    <br />
                    Sunday: Closed
                  </p>
                </div>

                <div>
                  <h3 className="font-display text-2xl font-semibold">Direct contact</h3>
                  <p className="mt-2 text-ink-muted">
                    Phone:{' '}
                    <a href="tel:+12017267342" className="font-semibold text-primary-700 hover:text-primary-900">
                      (201) 726-7342
                    </a>
                    <br />
                    Email:{' '}
                    <a
                      href="mailto:sheryl@sherylwilliams.com"
                      className="font-semibold text-primary-700 hover:text-primary-900"
                    >
                      sheryl@sherylwilliams.com
                    </a>
                  </p>
                </div>

                {locations.map((location) => (
                  <div key={location.name}>
                    <h3 className="font-display text-2xl font-semibold">{location.name}</h3>
                    <p className="mt-2 text-ink-muted">
                      {location.lines.map((line) => (
                        <span key={line}>
                          {line}
                          <br />
                        </span>
                      ))}
                    </p>
                    <div className="mt-4 h-52 overflow-hidden">
                      <iframe
                        title={`${location.name} map`}
                        src={location.map}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}

export default Contact;
